import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { Check, ChevronDown, Plus, Edit2, Trash2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from 'components/context/AuthContext';
import { useAdsModal } from 'components/context/ModalsContext';
import { useTopBarLeftContent } from 'components/context/TopBarLeftContentContext';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { useMetaAds } from 'hooks/useMetaAds';
import { toast } from 'react-hot-toast';
import { createPortal } from 'react-dom';
import { brandsApi, usersApi } from 'api';
import CreateBrandModal from 'components/modals/CreateBrandModal';
import EditBrandModal from 'components/modals/EditBrandModal';
import { GoogleIcon, MetaIcon, EsporaiTooltip } from 'components';

const FloatingBrandSelector = () => {
  const { auth, updateContextUser, hasGoogleAdsConnected, hasMetaConnected } = useContext(AuthContext);
  const { openConnectionsModal, isOptimizationsListModalOpen, isOptimizationDetailModalOpen } = useAdsModal();
  const { leftContent, isBrandSelectorVisible } = useTopBarLeftContent();
  const isAnyOptimizationModalOpen = isOptimizationsListModalOpen || isOptimizationDetailModalOpen;
  const { connectGoogleAds } = useGoogleAds();
  const { startMetaOAuth } = useMetaAds();
  const location = useLocation();
  const isOptimizadorRoute = location.pathname.includes('/optimizador');
  const isAdminPanelRoute = location.pathname.startsWith('/admin/panel');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const dropdownBtnRef = useRef();
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0, width: 0 });
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const filtersRef = useRef(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);

  const brands = useMemo(() => auth.user?.brands ?? [], [auth.user?.brands]);
  const activeBrandId = auth.user?.active_brand_id;
  const activeBrand = brands.find(b => b.id === activeBrandId);
  const isActiveBrandGoogleConnected = Boolean(activeBrand?.google_ads_connected);
  const isActiveBrandMetaConnected = Boolean(activeBrand?.meta_connected);
  const googleConnected = activeBrand ? isActiveBrandGoogleConnected : hasGoogleAdsConnected;
  const metaConnected = activeBrand ? isActiveBrandMetaConnected : hasMetaConnected;
  // La marca activa siempre aparece arriba en el dropdown
  const sortedBrands = useMemo(() => {
    if (!activeBrandId) return brands;
    return [...brands].sort((a, b) => {
      if (a.id === activeBrandId) return -1;
      if (b.id === activeBrandId) return 1;
      return 0;
    });
  }, [brands, activeBrandId]);

  useEffect(() => {
    if (isDropdownOpen && dropdownBtnRef.current) {
      const rect = dropdownBtnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
        width: Math.max(200, rect.width),
      });
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isFiltersOpen) return;
    const onMouseDown = (e) => {
      if (!filtersRef.current) return;
      if (!filtersRef.current.contains(e.target)) setIsFiltersOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsFiltersOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isFiltersOpen]);

  useEffect(() => {
    if (!isOptimizadorRoute) setIsFiltersOpen(false);
  }, [isOptimizadorRoute]);

  const handleBrandChange = async (brandId) => {
    if (brandId === auth.user?.active_brand_id) {
      setIsDropdownOpen(false);
      return;
    }

    setChanging(true);
    try {
      const response = await brandsApi.setActiveBrand(brandId);
      updateContextUser(response.data);
      toast.success('Marca activa cambiada');
      setIsDropdownOpen(false);
      window.location.reload();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setChanging(false);
    }
  };

  const handleCreateBrand = async (brandData) => {
    try {
      await brandsApi.createBrand(brandData);
      toast.success('Marca creada exitosamente');
      const userResponse = await usersApi.getCurrentUser();
      updateContextUser(userResponse.data);
      setIsDropdownOpen(false);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error al crear marca: ${errorMessage}`);
      throw error;
    }
  };

  const handleUpdateBrand = async (brandData) => {
    if (!editingBrand) return;
    try {
      await brandsApi.updateBrand(editingBrand.id, brandData);
      toast.success('Marca actualizada exitosamente');
      const userResponse = await usersApi.getCurrentUser();
      updateContextUser(userResponse.data);
      setEditingBrand(null);
      setIsDropdownOpen(false);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error al actualizar marca: ${errorMessage}`);
      throw error;
    }
  };

  const handleDeleteBrand = async (e, brand) => {
    e.stopPropagation();
    if (brand.id === auth.user?.active_brand_id) return;
    if (!window.confirm(`¿Eliminar la marca "${brand.name}"?`)) return;
    try {
      await brandsApi.deleteBrand(brand.id);
      toast.success('Marca eliminada');
      const userResponse = await usersApi.getCurrentUser();
      updateContextUser(userResponse.data);
      setIsDropdownOpen(false);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error al eliminar marca: ${errorMessage}`);
    }
  };

  if (!isBrandSelectorVisible || brands.length === 0) {
    return null;
  }

  const ConnectionBadge = ({ connected, icon: Icon, label, onClick }) => {
    const platformName = label === 'Google Ads' ? 'Google' : 'Meta';
    const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 1024;

    const wrapper = (
      <button
        type="button"
        onClick={onClick}
        className={`
          flex items-center gap-1.5 lg:gap-2 px-1.5 lg:px-2.5 py-1.5 lg:py-2 rounded-lg transition-all duration-200 cursor-pointer shrink-0
          ${connected
            ? 'bg-white/10 border border-white/20 hover:bg-white/15'
            : 'bg-white/5 border border-white/10 hover:bg-white/15 hover:border-amber-400/40'
          }
        `}
      >
        <span className="relative shrink-0">
          <Icon width={isSmallScreen ? 16 : 19} height={isSmallScreen ? 16 : 19} className={connected ? 'opacity-100' : 'opacity-50'} />
          <span
            className={`absolute -bottom-0.5 -right-0.5 w-2 lg:w-2.5 h-2 lg:h-2.5 rounded-full ring-2 ring-slate-900/80 ${
              connected ? 'bg-emerald-400' : 'bg-red-500'
            }`}
          />
        </span>
        <span className={`text-xs lg:text-sm font-medium whitespace-nowrap hidden lg:inline ${connected ? 'text-emerald-400' : 'text-white/60'}`}>
          {connected ? 'Conectado' : `Conectar ${platformName}`}
        </span>
      </button>
    );
    return (
      <EsporaiTooltip content={connected ? `${label} conectado` : `Conectar ${label}`} position="bottom">
        {wrapper}
      </EsporaiTooltip>
    );
  };

  return (
    <>
      <div
        className={`hidden lg:flex fixed top-4 left-0 right-0 min-h-[3.5rem] flex-wrap items-center gap-2 lg:gap-3 pl-5 pr-4 sm:pl-[max(1.5rem,calc(var(--sidebar-width,0px)+1.5rem))] sm:pr-4 sm:w-full transition-all duration-300 ${
          isAnyOptimizationModalOpen ? 'z-40 pointer-events-none' : 'z-[9000]'
        }`}
      >
        {/* Contenido opcional (p. ej. filtros campañas) separado de badges + selector para evitar solapamiento */}
        <div className="flex items-center justify-end w-full min-w-0 gap-3 lg:gap-5 xl:gap-6 flex-wrap gap-y-2">
          {leftContent ? (
            <div className="min-w-0 shrink max-w-[min(100%,calc(100vw-var(--sidebar-width,0px)-30rem))] pr-2">
              {leftContent}
            </div>
          ) : null}
          <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0 pl-1">
            {!isAdminPanelRoute && (
              <>
                <ConnectionBadge
                  connected={googleConnected}
                  icon={GoogleIcon}
                  label="Google Ads"
                  onClick={googleConnected ? openConnectionsModal : () => connectGoogleAds(false)}
                />
                <ConnectionBadge
                  connected={metaConnected}
                  icon={MetaIcon}
                  label="Meta Ads"
                  onClick={metaConnected ? openConnectionsModal : () => startMetaOAuth(false)}
                />
              </>
            )}
            <button
              data-tour="brand-selector"
              ref={dropdownBtnRef}
              type="button"
              onClick={() => !changing && setIsDropdownOpen(!isDropdownOpen)}
              disabled={changing}
              title="Cambiar marca activa"
              className={`
            flex items-center gap-1.5 lg:gap-2 px-1.5 lg:px-2.5 py-1.5 lg:py-2 rounded-lg transition-all duration-200 flex-shrink-0 min-w-0 mr-2 lg:mr-4
            ${changing
              ? 'opacity-50 cursor-not-allowed bg-white/5 border border-white/10'
              : isDropdownOpen
                ? 'cursor-pointer bg-white/15 border border-white/30 text-white'
                : 'cursor-pointer bg-white/10 border border-white/20 hover:bg-white/15 text-white/90 hover:text-white'
            }
          `}
            >
              <span className={`text-xs lg:text-sm font-medium truncate max-w-[70px] md:max-w-[100px] lg:max-w-[160px] ${
                changing ? 'text-white/50' : 'text-inherit'
              }`}>
                {changing ? 'Cambiando...' : (activeBrand?.name || 'Seleccionar')}
              </span>
              <ChevronDown
                size={16}
                className={`shrink-0 text-white/60 transition-transform duration-200 ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      {isDropdownOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[100000]"
            onClick={() => setIsDropdownOpen(false)}
          />
          <div
            className="fixed z-[100001] backdrop-blur-xl bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 rounded-2xl shadow-2xl max-h-80 overflow-hidden border border-white/20 min-w-56"
            style={{
              top: `${dropdownPos.top}px`,
              right: `${dropdownPos.right}px`,
              width: `${dropdownPos.width}px`,
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <div className="max-h-64 overflow-y-auto p-1">
              {sortedBrands.map((brand) => {
                const isActive = brand.id === auth.user?.active_brand_id;
                return (
                  <button
                    key={brand.id}
                    type="button"
                    onClick={() => handleBrandChange(brand.id)}
                    disabled={changing}
                    className={`
                      w-full px-4 py-3 text-left text-sm flex items-center justify-between
                      disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200
                      rounded-lg my-0.5 border-b border-white/5 last:border-b-0
                      ${isActive
                        ? 'bg-white/20 backdrop-blur-md text-white font-semibold border border-white/30 shadow-sm'
                        : 'hover:bg-white/10 text-white/90 hover:text-white'
                      }
                    `}
                  >
                    <span className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 flex-shrink-0">
                        <span title={brand.google_ads_connected ? 'Google Ads conectado' : 'Google Ads no conectado'}>
                          <span className="relative inline-block">
                            <GoogleIcon width={14} height={14} className={brand.google_ads_connected ? 'opacity-100' : 'opacity-50'} />
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ring-2 ring-slate-900/80 ${
                                brand.google_ads_connected ? 'bg-emerald-400' : 'bg-red-400'
                              }`}
                            />
                          </span>
                        </span>
                        <span title={brand.meta_connected ? 'Meta Ads conectado' : 'Meta Ads no conectado'}>
                          <span className="relative inline-block">
                            <MetaIcon width={14} height={14} className={brand.meta_connected ? 'opacity-100' : 'opacity-50'} />
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ring-2 ring-slate-900/80 ${
                                brand.meta_connected ? 'bg-emerald-400' : 'bg-red-400'
                              }`}
                            />
                          </span>
                        </span>
                      </span>
                      <span className="group/brand flex items-center gap-1.5 min-w-0 flex-1">
                        <span className="truncate flex-1">{brand.name}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingBrand(brand);
                          }}
                          className="opacity-0 group-hover/brand:opacity-100 p-0.5 hover:bg-white/20 rounded transition-opacity flex-shrink-0"
                          title="Editar marca"
                        >
                          <Edit2 className="h-3 w-3 text-white/80" />
                        </button>
                        {!isActive && (
                          <button
                            type="button"
                            onClick={(e) => handleDeleteBrand(e, brand)}
                            className="opacity-0 group-hover/brand:opacity-100 p-0.5 hover:bg-red-500/30 rounded transition-opacity flex-shrink-0"
                            title="Eliminar marca"
                          >
                            <Trash2 className="h-3 w-3 text-red-300" />
                          </button>
                        )}
                      </span>
                    </span>
                    {isActive && (
                      <span className="flex items-center gap-1">
                        <Check className="h-4 w-4 text-white flex-shrink-0" />
                      </span>
                    )}
                  </button>
                );
              })}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(false);
                  setIsCreateModalOpen(true);
                }}
                className="w-full px-4 py-3 text-left text-sm flex items-center gap-3 rounded-lg my-0.5 border border-dashed border-white/30 hover:bg-white/10 hover:border-white/50 text-white/90 hover:text-white transition-all duration-200"
              >
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/30 border border-blue-400/50">
                  <Plus size={14} className="text-blue-200" />
                </span>
                <span className="font-medium">Nueva Marca</span>
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      <CreateBrandModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateBrand={handleCreateBrand}
      />

      <EditBrandModal
        isOpen={!!editingBrand}
        onClose={() => setEditingBrand(null)}
        brand={editingBrand}
        onUpdateBrand={handleUpdateBrand}
      />
    </>
  );
};

export default FloatingBrandSelector;
